import { inject, Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { catchError, EMPTY, map, Observable, take } from 'rxjs';
import { emailIntegrationUrl } from './email-service.queries';

import { IIncident } from '@app/pages/cmms/interfaces/incidents.interface';
import { ICMMSLocation } from '@app/pages/cmms/interfaces/cmms-global.interfaces';
import { IAuComment } from '@app/shared/ui-components/au-comments/i-au.comment';
import { formatDate } from '@angular/common';
import { IMessageText } from '@app/pages/messages/interfaces/messages.interfaces';
import { setMessageExternalDate } from '@app/pages/messages/state/messages.action';
import { Store } from '@ngrx/store';
import { IWorkOrder } from '@app/domain/cmms-types/workorder-types';

@Injectable({
	providedIn: 'root',
})
export class EmailService {
	private readonly apollo = inject(Apollo);
	private readonly store$ = inject(Store);

	getIntergationUrl(): Observable<string> {
		return this.apollo
			.query({
				query: emailIntegrationUrl,
				fetchPolicy: 'no-cache',
			})
			.pipe(
				take(1),
				map((res: any) => {
					return res.data.outlookAppSettings?.authUrl;
				}),
				catchError((error: any) => {
					console.error(error);
					return EMPTY;
				})
			);
	}

	refactorComments(comments: IAuComment[]): string[] {
		if (!comments.length) {
			return [];
		}
		return comments
			.map(comment => {
				const createdDate = formatDate(comment.created, 'dd-MM-yyyy', 'en-US');
				return `${createdDate} - ${comment.author.displayName}:\n • “${comment.text}”`;
			})
			.reverse();
	}

	formatSubjectAndBody(entity: IWorkOrder | IIncident, id: string): string {
		const comments = this.refactorComments(entity.comments);
		const commentsText = comments.length ? comments.join('\n\n') : '';
		const htmlDescription = `${id}\n\n${entity.description ? `${entity.description}\n\n` : ''}${commentsText}`;
		if (!htmlDescription) return '';
		try {
			const doc = new DOMParser().parseFromString(htmlDescription, 'text/html');
			return doc.body.textContent || '';
		} catch (e) {
			return htmlDescription
				.replace(/<[^>]*>?/gm, '')
				.replace(/&nbsp;/g, ' ')
				.replace(/\s+/g, ' ')
				.trim();
		}
	}

	setMessageExternalData(
		entity: IWorkOrder | IIncident,
		id: string,
		locationAncestry: Partial<ICMMSLocation>[],
		attachedFiles: { pk: string; name: string }[]
	) {
		const locations = locationAncestry.length ? locationAncestry : entity.locations;
		const locationsText = locations.length
			? locations.map(location => location.name).join(', ') + ': '
			: '';
		const emailText: string = this.formatSubjectAndBody(entity, id);
		const messageText: { messageText: IMessageText } = {
			messageText: {
				recipients: null,
				subject: locationsText + entity.name,
				text: emailText,
				files: attachedFiles,
			},
		};
		this.store$.dispatch(setMessageExternalDate(messageText));
	}
}
